/* global $ */
import { Fragment } from 'preact';
import { useState, useRef, useCallback } from 'preact/hooks';
import { Text, withText } from 'preact-i18n';
import { withGrowthBookFeatures } from '../../context/GrowthBook';
import { selectIcon } from '../../templates/place';
import DatesControls from './components/DatesControls';
import Calendar from './components/Calendar';
import usePickerEventHandlers from './hooks/usePickerEventHandlers';
import { trackCalendarButtonClick } from './utils/tracking';
import useCalendarEvents from './hooks/useCalendarEvents';
import { getVisibleMonth } from './utils/pickerHelpers';
import { calculateDateWindow } from './utils/calendarServiceHelpers';
import stringToDate from '../../utils/stringToDate';
import getFirstAvailableDate from '../../utils/getFirstAvailableDate';

/**
 * Datepicker Component - Functional version
 * Manages date selection using pickadate library
 */
const DatePicker = props => {
  const {
    // Core props
    field,
    error,
    // eslint-disable-next-line no-unused-vars
    placeholder, // This is used in the placeholderContext
    placeholderContext,
    label,

    // Date props
    date,
    returnDate,
    dateFormat,
    maxDaysSearch,
    firstAvailableDate,
    skipInitialValidation = false,

    // Picker configuration
    way, // 'departure' or 'return'
    calendarOpen, // Used in Calendar component
    hide = false,
    disabled = false,
    autoOpenCalendar, // Used in Calendar component
    showMundialButton = false, // AB test flag to show Mundial button
    worldCupButtonIcon,

    // Calendar events props
    originSlug, // Origin slug (e.g., 'cdmx')
    destinationSlug, // Destination slug (e.g., 'monterrey')
    sourceUrl, // Source URL from config (e.g., 'www.reservamos.mx/api/v2')
    forceHttps = false, // Force HTTPS protocol

    // Callbacks
    onFocus,
    onClose,
    onSelect,
    onDatePickerCreated,
    onOpenTicketDate, // Used in hooks
    setDeparturePicker,

    // Text props (from withText) - Used in helper functions
    openTicketText,
    openTicketAdText,
    openTicketDepartureText,
    openTicketReturnText,
    newText,
    departureDatePickerText,
    returnDatePickerText,
    title,

    // GrowthBook features
    growthbookFeatures = {},
  } = props;

  // State management
  const [isFocused, setIsFocused] = useState(false);
  const [dateWindow, setDateWindow] = useState(null); // 90-day window for calendar events { startDate, endDate }

  // Refs to maintain mutable values across renders
  const fieldWrapperRef = useRef(null);
  const compactDateUsedRef = useRef(false);
  const calendarButtonClickedRef = useRef('');
  const $inputRef = useRef(null);

  // Computed values
  // While the first days are blocked, only the "today" and "tomorrow" shortcuts are suppressed:
  // pickadate moves a selection outside the range to the first enabled date, so those buttons
  // would set a date that does not match their own label. The row itself stays — "Elegir" only
  // opens the calendar — and search.js keys the `compact-date` layout class off the same
  // conditions as this flag, so hiding the row here would leave that class pointing at nothing.
  const hasBlockedDates = Boolean(getFirstAvailableDate(firstAvailableDate, maxDaysSearch));
  const showCompactDate =
    !date && field === 'departureDate' && !returnDate && !compactDateUsedRef.current;
  const showCleanButton = Boolean(date);

  // Event handlers for picker interactions (created before Calendar initialization)
  const {
    handleChangeDepartureDate,
    handleChangeReturnDate,
    handleClean,
    handleAlternativeDatesClick,
    handleOnDateButtonClick,
    handleOnLabelClick,
    handleMundialClick,
  } = usePickerEventHandlers({
    $inputRef,
    field,
    openTicketText,
    maxDaysSearch,
    firstAvailableDate,
    dateFormat,
    onClose,
    onSelect,
    compactDateUsedRef,
    calendarButtonClickedRef,
    showCompactDate,
  });

  // Calendar lifecycle callbacks
  const calendarCallbacks = {
    onOpen: useCallback(() => {
      setIsFocused(true);

      // Close the other datepicker if it's open
      // This ensures only one calendar is open at a time
      const otherField = field === 'departureDate' ? 'returnDate' : 'departureDate';
      try {
        const $otherInput = $(`input.${otherField}`);
        if ($otherInput.length) {
          const otherPicker = $otherInput.pickadate('picker');
          if (otherPicker && otherPicker.get('open')) {
            otherPicker.close();
          }
        }
      } catch (err) {
        // Silently fail if other picker is not initialized
      }

      // Get visible month when calendar opens and calculate date window
      const visibleMonth = getVisibleMonth($inputRef.current);
      if (visibleMonth) {
        const window = calculateDateWindow(visibleMonth);
        setDateWindow(window);
      }
    }, [field]),
    onClose: useCallback(
      data => {
        if (onClose) onClose(data.value);
        setIsFocused(false);
      },
      [onClose]
    ),
    onSet: useCallback(
      ({ field: setField, value }) => {
        // Only set compactDateUsed if there's actually a date value selected
        // onSet can fire when navigating months, but we only care when a date is actually selected
        if (value && value.trim() !== '') {
          if (onSelect) onSelect('personalized');
          trackCalendarButtonClick('personalized', calendarButtonClickedRef.current);
          compactDateUsedRef.current = true;

          // Update startDate when a date is selected (for calendar events)
          // This ensures the calendar events hook fetches events for the correct month
          // even when the calendar is closed after selection (e.g., "Today" or "Tomorrow" buttons)
          if (setField === 'departureDate') {
            // Try to get visible month from picker first (works when calendar is open)
            let visibleMonth = getVisibleMonth($inputRef.current);

            // If calendar is closed, get month from the selected date value
            if (!visibleMonth && value) {
              const selectedDate = stringToDate(value, dateFormat);
              if (selectedDate) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Check if selected date is in current month
                const isCurrentMonth =
                  selectedDate.getFullYear() === today.getFullYear() &&
                  selectedDate.getMonth() === today.getMonth();

                if (isCurrentMonth) {
                  visibleMonth = new Date(today);
                } else {
                  // Use first day of the selected date's month
                  visibleMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                }
              }
            }

            if (visibleMonth) {
              const window = calculateDateWindow(visibleMonth);
              setDateWindow(window);
            }
          }
        }
      },
      [onSelect, calendarButtonClickedRef, dateFormat]
    ),
    onMonthChange: useCallback(({ visibleMonth }) => {
      if (visibleMonth) {
        const window = calculateDateWindow(visibleMonth);
        setDateWindow(window);
      }
    }, []),
  };

  // Calendar event handlers (for custom events)
  const calendarEventHandlers = {
    onChangeDepartureDate: handleChangeDepartureDate,
    onChangeReturnDate: handleChangeReturnDate,
    onClean: handleClean,
  };

  // Calendar configuration
  const calendarConfig = {
    field,
    maxDaysSearch,
    firstAvailableDate,
    dateFormat,
    hide,
    skipInitialValidation,
    way,
    calendarOpen,
    date,
    autoOpenCalendar,
    openTicketText,
    openTicketAdText,
    openTicketDepartureText,
    openTicketReturnText,
    newText,
    departureDatePickerText,
    returnDatePickerText,
    title,
    onOpenTicketDate,
  };

  // Calendar options
  const calendarOptions = {
    setDeparturePicker,
    onDatePickerCreated,
    showMundialButton,
  };

  const calendarEvents = useCalendarEvents({
    origin: originSlug,
    destination: destinationSlug,
    startDate: dateWindow?.startDate,
    endDate: dateWindow?.endDate,
    enabled: Boolean(originSlug && destinationSlug && sourceUrl),
    options: {
      sourceUrl,
      forceHttps,
    },
  });

  const eventsToDisplay = calendarEvents.events || [];

  return (
    <div
      className={`${field}-wrapper ${isFocused ? 'focused' : ''}`}
      key={`${field}-wrapper`}
      ref={fieldWrapperRef}
      onClick={handleOnDateButtonClick}
    >
      {/* Calendar component - invisible, manages pickadate instance */}
      <Calendar
        inputRef={$inputRef}
        fieldWrapperRef={fieldWrapperRef}
        config={calendarConfig}
        callbacks={calendarCallbacks}
        eventHandlers={calendarEventHandlers}
        options={calendarOptions}
        events={eventsToDisplay}
      />

      <Fragment>
        {error && (
          <div
            className="icon-error"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: selectIcon('error', false),
            }}
          />
        )}

        {showCompactDate && (
          <DatesControls
            label={<Text id="label.when_traveling" />}
            onDateClick={handleOnDateButtonClick}
            onTodayClick={handleAlternativeDatesClick('today')}
            onTomorrowClick={handleAlternativeDatesClick('tomorrow')}
            onMundialClick={handleMundialClick}
            showMundialButton={showMundialButton}
            showTomorrowWithMundial={growthbookFeatures?.show_tomorrow_with_mundial !== false}
            showQuickDates={!hasBlockedDates}
            worldCupButtonIcon={worldCupButtonIcon}
          />
        )}

        {!showCompactDate && (
          <label
            className={`label label-dates ${error ? 'label-error' : ''}`}
            onClick={handleOnLabelClick}
          >
            <Text id={`label.${label}`} />
          </label>
        )}

        <input
          role="combobox"
          aria-readonly="true"
          aria-label={placeholderContext}
          type="text"
          className={`${field} ${showCompactDate ? 'hide' : ''}`}
          placeholder={placeholderContext}
          ct-datepicker="true" // Clicktripz
          onFocus={onFocus}
          disabled={disabled}
          id={`txtDate-${field}`}
          data-hj-allow
        />

        {showCleanButton && (
          <div
            className="clean-place-input"
            onClick={handleClean}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: selectIcon('cleanIcon', false),
            }}
          />
        )}

        {!showCompactDate && !date && (
          <div
            class="iqdropdown-selection"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: selectIcon('arrow', false),
            }}
          />
        )}
      </Fragment>
    </div>
  );
};

export default withText(props => ({
  placeholderContext: <Text id={`placeholder.${props.placeholder}`} />,
  openTicketText: <Text id={`label.open_ticket`} />,
  openTicketAdText: <Text id={`label.open_ticket_ad`} />,
  openTicketReturnText: <Text id={`label.open_ticket_return`} />,
  openTicketDepartureText: <Text id={`label.open_ticket_departure`} />,
  newText: <Text id={`label.new`} />,
  // These props are passed from parent and not translated
  departureDatePickerText: props.departureDatePickerText,
  returnDatePickerText: props.returnDatePickerText,
  title: props.title,
}))(withGrowthBookFeatures(DatePicker));
